import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import toast from 'react-hot-toast';
import { supabase, fetchFollowedLabels, fetchUnfollowedLabels, fetchAllLabels, submitLabelUrl } from '../utils/supabase';

// Define our own Label interface for this component
interface Label {
  id: number;
  created_at: string;
  label_name: string;
  label_url: string;
  tags?: string[] | null;
}

type TabType = 'all' | 'discover' | 'following';

const ITEMS_PER_PAGE = 20;

const Labels: React.FC = () => {
  const { 
    userId, 
    isAuthenticated,
    isLoading: contextLoading,
    error: contextError
  } = useAppContext();

  const [labels, setLabels] = useState<Label[]>([]);
  const [followedLabels, setFollowedLabels] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingLabelId, setLoadingLabelId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [lastToggleTime, setLastToggleTime] = useState<number>(0);
  // Search term states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [labelUrl, setLabelUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPageLoading = loading || contextLoading;
  const displayError = error || contextError;

  // Create a callback ref for infinite scrolling
  const observer = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef(false); // Track loading state in a ref to avoid dependency issues
  
  // Modal ref for handling clicks outside the modal
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Add request cancellation reference
  const currentRequestIdRef = useRef<number | null>(null);
  
  // Add a reference for the search timeout
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Function to handle opening the modal
  const openModal = useCallback(() => {
    setIsModalOpen(true);
    setLabelUrl('');
    // Prevent background scrolling when modal is open
    document.body.style.overflow = 'hidden';
  }, []);
  
  // Function to handle closing the modal
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setLabelUrl('');
    // Restore scrolling when modal is closed
    document.body.style.overflow = 'auto';
  }, []);
  
  // Function to handle submitting the label URL
  const handleSubmitLabel = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!labelUrl.trim()) {
      toast.error('Please enter a valid Bandcamp label URL');
      return;
    }
    
    // Basic validation - must include bandcamp.com
    if (!labelUrl.includes('bandcamp.com')) {
      toast.error('Please enter a valid Bandcamp label URL');
      return;
    }
    
    // Normalize the URL for validation
    let normalizedUrl = labelUrl.toLowerCase().trim().replace(/\/+$/, '');
    
    try {
      const url = new URL(normalizedUrl);
      
      // If it's a subdomain.bandcamp.com URL, normalize to just the root domain
      if (url.hostname.endsWith('.bandcamp.com') && url.hostname !== 'bandcamp.com') {
        // Keep only the hostname part for subdomain URLs (e.g., allmythoughts.bandcamp.com)
        normalizedUrl = `${url.protocol}//${url.hostname}`;
      } else if (url.hostname === 'bandcamp.com' && url.pathname.startsWith('/label/')) {
        // For bandcamp.com/label/xxx URLs, keep only the /label/xxx part
        const labelPathMatch = url.pathname.match(new RegExp('^/label/([^/]+)'));
        if (labelPathMatch) {
          normalizedUrl = `${url.protocol}//${url.hostname}/label/${labelPathMatch[1]}`;
        }
      }
    } catch (error) {
      console.error('Error parsing URL:', error);
      toast.error('Please enter a valid URL');
      return;
    }
    
    // Enhanced validation for Bandcamp label URLs
    const labelDomainPattern = new RegExp('^https?://[a-z0-9-]+\\.bandcamp\\.com/?$', 'i');
    const labelPathPattern = new RegExp('^https?://bandcamp\\.com/label/[a-z0-9-]+/?$', 'i');
    
    if (!labelDomainPattern.test(normalizedUrl) && !labelPathPattern.test(normalizedUrl)) {
      toast.error('The URL does not appear to be a valid Bandcamp label URL. Please provide a URL like "https://labelname.bandcamp.com" or "https://bandcamp.com/label/labelname".');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (!userId) {
        throw new Error('You must be logged in to submit a label');
      }
      
      // Call the submitLabelUrl function from supabase.ts
      const result = await submitLabelUrl(labelUrl, userId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit label');
      }
      
      // Handle the case where the label was already submitted
      if (result.success && result.error) {
        toast.success(result.error, {
          duration: 3000,
          position: 'bottom-center',
          style: {
            background: '#3B82F6', // Blue color for informational messages
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '8px',
          },
        });
        
        closeModal();
        return;
      }
      
      toast.success('Label submitted successfully! New label submissions are processed every day. Thank you for your submission!', {
        duration: 3000,
        position: 'bottom-center',
        style: {
          background: '#10B981',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: '8px',
        },
      });
      
      // Just close the modal without refreshing any data
      closeModal();
      
      // When we implement the full functionality, we might want to update the UI
      // if the label was successfully added to the database. Here's how we could
      // handle different scenarios without causing a full refresh:
      
      // 1. If the label already existed in the database:
      //    - No need to update the UI, just show a success message
      
      // 2. If the label was newly added and we're in the "discover" tab:
      //    - We could add the new label to the local state if we get back the label details
      //    - This would avoid a full refetch while still updating the UI
      //    if (result.labelId && activeTab === 'discover' && result.labelDetails) {
      //      setLabels(prevLabels => [result.labelDetails, ...prevLabels]);
      //      setTotalCount(prev => prev !== null ? prev + 1 : null);
      //    }
      
      // 3. If the label was newly added and we're in the "following" tab:
      //    - No need to update the UI since the user isn't following it yet
      //    - They would need to switch to "discover" and follow it
      
    } catch (error) {
      console.error('Error submitting label:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit label. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle clicks outside the modal to close it - use useCallback to prevent unnecessary re-renders
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        closeModal();
      }
    };
    
    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen, closeModal]);
  
  const lastLabelElementRef = useCallback((node: HTMLDivElement) => {
    if (loadingRef.current || !hasMore) return;
    
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
        setPage(prevPage => prevPage + 1);
      }
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // Trigger when 10% of the element is visible
    });
    if (node) observer.current.observe(node);
    // We intentionally omit 'page' from dependencies to prevent constant re-renders
    // and infinite loops. We use setPage with a function to ensure we always get the latest state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore]);

  // Create a fetchLabelsForTab function that takes the tab as a parameter to avoid stale closure issues
  const fetchLabelsForTab = useCallback(
    async (
      tabToFetch: TabType,
      pageToFetch: number = 1,
      letterFilter: string | null = null,
      tagFilter: string | null = null
    ) => {
      if (!isAuthenticated || !userId) {
        return;
      }

      // Create a unique identifier for this request
      const requestId = Date.now();
      currentRequestIdRef.current = requestId;

      // Skip the count query on pagination requests (page > 1) since we already have totalCount
      const skipCount = pageToFetch > 1;

      try {
        setLoading(true);
        loadingRef.current = true; // Set loading ref to true
        setError(null); // Reset error state at the start of a new request

        let result;

        // Use the appropriate function based on the provided tab
        if (tabToFetch === "following") {
          result = await fetchFollowedLabels(
            pageToFetch,
            ITEMS_PER_PAGE,
            null, // Remove letter filter
            tagFilter,
            debouncedSearchTerm || null,
            userId,
            skipCount
          );
        } else if (tabToFetch === "discover") {
          result = await fetchUnfollowedLabels(
            pageToFetch,
            ITEMS_PER_PAGE,
            null, // Remove letter filter
            tagFilter,
            debouncedSearchTerm || null,
            userId,
            skipCount
          );
        } else {
          // 'all' tab - fetch all labels
          result = await fetchAllLabels(
            pageToFetch,
            ITEMS_PER_PAGE,
            null, // Remove letter filter
            tagFilter,
            debouncedSearchTerm || null,
            userId,
            skipCount
          );
        }

        // If another request came in after we started, don't update state
        if (currentRequestIdRef.current !== requestId) {
          return;
        }

        const { data: newLabels, count } = result;

        if (!newLabels || newLabels.length === 0) {
          if (pageToFetch === 1) {
            setLabels([]);
          }
          setHasMore(false);
          return;
        }

        // Only append data if this is not the first page and we got new data
        if (pageToFetch > 1) {
          setLabels((prevLabels) => [...prevLabels, ...newLabels]);
        } else {
          // Replace data completely on first page
          setLabels(newLabels);
        }

        // Set total count if we got it
        if (count !== undefined) {
          setTotalCount(count);

          // Calculate hasMore precisely
          const currentItems = (pageToFetch - 1) * ITEMS_PER_PAGE + newLabels.length;
          const hasMoreItems = currentItems < count;
          setHasMore(hasMoreItems);
        } else {
          setHasMore(newLabels.length >= ITEMS_PER_PAGE);
        }
      } catch (error) {
        // Only update error state if we're still the active request
        if (currentRequestIdRef.current === requestId) {
          setError(`Error fetching ${tabToFetch} labels`);
          setHasMore(false);
          if (pageToFetch === 1) {
            setLabels([]);
          }
        }
      } finally {
        // Only update loading state if we're still the active request
        if (currentRequestIdRef.current === requestId) {
          setLoading(false);
          loadingRef.current = false; // Reset loading ref
        }
      }
    },
    [isAuthenticated, userId, debouncedSearchTerm]
  );

  // Wrapper for fetching with current state values
  const fetchLabels = useCallback(() => {
    return fetchLabelsForTab(activeTab, page, null, selectedTag);
  }, [fetchLabelsForTab, activeTab, page, selectedTag]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Add debounce to prevent too many API calls
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 600); // Increased debounce to 600ms for better performance
  };

  // Single consolidated effect for fetching when search term, tab, or tag changes
  useEffect(() => {
    if (!isAuthenticated) return;

    // Reset pagination when any filter changes
    setPage(1);
    setLabels([]);
    setTotalCount(null);
    setHasMore(true);

    // Fetch data with current filters
    fetchLabelsForTab(activeTab, 1, null, selectedTag);
  }, [debouncedSearchTerm, activeTab, selectedTag, isAuthenticated, fetchLabelsForTab]);

  // Handle tag filter click
  const handleTagClick = (tag: string) => {
    // Toggle tag selection (clicking the same tag again clears the filter)
    setSelectedTag(selectedTag === tag ? null : tag);
  };

  // Handle tab switch - just update state, the useEffect handles fetching
  const handleTabSwitch = (newTab: TabType) => {
    if (newTab === activeTab) return;
    setActiveTab(newTab);
  };

  // Only fetch when page changes (not on tab/filter changes - those are handled separately)
  useEffect(() => {
    // Skip the initial fetch (page 1 is handled by tab/filter changes)
    if (page > 1 && isAuthenticated && !loadingRef.current) {
      fetchLabels();
    }
  }, [page, fetchLabels, isAuthenticated]);

  const fetchFollowedLabelIds = async () => {
    if (!isAuthenticated || !userId) return;
    
    try {
      const { data, error } = await supabase
        .from('user_follow_labels')
        .select('label_id')
        .eq('user_id', userId);

      if (error) {
        setFollowedLabels(new Set());
        return;
      }
      
      if (data) {
        const followedLabelIds = new Set(data.map(item => item.label_id));
        setFollowedLabels(followedLabelIds);
      }
    } catch (error) {
      console.error('Error fetching followed labels:', error);
      setFollowedLabels(new Set());
    }
  };

  const toggleFollow = async (labelId: number) => {
    if (!userId || !isAuthenticated) return;
    
    // Prevent clicking the same label that's currently loading
    if (loadingLabelId === labelId) {
      return;
    }

    // Check for rate limiting by enforcing a minimum interval between requests
    const now = Date.now();
    const timeSinceLastToggle = now - lastToggleTime;
    
    // Apply rate limiting
    if (timeSinceLastToggle < 300) {
      toast.error('Please slow down, you\'re toggling too fast!', {
        duration: 3000,
        position: 'bottom-center',
      });
      return;
    }
    
    // Store the previous state in case we need to revert
    const wasFollowed = followedLabels.has(labelId);
    
    try {
      // Update local state first for immediate UI feedback
      setFollowedLabels(prev => {
        const newSet = new Set(prev);
        if (newSet.has(labelId)) {
          newSet.delete(labelId);
        } else {
          newSet.add(labelId);
        }
        return newSet;
      });
      
      // Set loading state
      setLoadingLabelId(labelId);
      setLastToggleTime(now);
      setError(null);
      
      if (wasFollowed) {
        // Unfollow the label
        const { error: unfollowError } = await supabase
          .from('user_follow_labels')
          .delete()
          .eq('user_id', userId)
          .eq('label_id', labelId);
          
        if (unfollowError) throw unfollowError;
      } else {
        // Follow the label
        const { error: followError } = await supabase
          .from('user_follow_labels')
          .insert({ user_id: userId, label_id: labelId });
          
        if (followError) throw followError;
      }
      
      // Update the UI locally based on which tab we're on
      if (activeTab === 'following' && wasFollowed) {
        // In the Following tab, remove the label that was unfollowed
        setLabels(prevLabels =>
          prevLabels.filter(label => label.id !== labelId)
        );

        // Update total count
        if (totalCount !== null) {
          setTotalCount(totalCount - 1);
        }
      } else if (activeTab === 'discover' && !wasFollowed) {
        // In the Discover tab, remove the label that was followed
        setLabels(prevLabels =>
          prevLabels.filter(label => label.id !== labelId)
        );

        // Update total count
        if (totalCount !== null) {
          setTotalCount(totalCount - 1);
        }
      }
      // In the 'all' tab, don't remove the label - just let the state update
      // The button will automatically update based on followedLabels set
      
      // Show success toast
      toast.success(wasFollowed ? 'Unfollowed label' : 'Following label', {
        duration: 1500,
        position: 'bottom-center',
        style: {
          background: wasFollowed ? '#6B7280' : '#10B981',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: '8px',
        },
      });
      
    } catch (error) {
      console.error('Error toggling follow status:', error);
      
      // Revert local state on error
      setFollowedLabels(prev => {
        const newSet = new Set(prev);
        if (wasFollowed) {
          newSet.add(labelId);
        } else {
          newSet.delete(labelId);
        }
        return newSet;
      });
      
      setError('Failed to update follow status');
      toast.error('Failed to update. Please try again.', {
        duration: 3000,
        position: 'bottom-center',
      });
    } finally {
      setLoadingLabelId(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchFollowedLabelIds();
    }
    // We intentionally omit fetchFollowedLabelIds from dependencies as it doesn't change
    // and we only want to run this effect when authentication state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Cleanup function for intersection observer
  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  // Handle modal mounting/unmounting effects
  useEffect(() => {
    // When component unmounts, ensure we restore scrolling
    return () => {
      document.body.style.overflow = 'auto';
      
      // Clear any pending search timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <main className="pt-16 pb-6 px-6 max-w-[1080px] mx-auto">
        <div className="mb-8 border-b border-violet-300 pb-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">Labels</h1>
              <p className="text-gray-700">Manage the labels you follow</p>
            </div>
            <button
              onClick={openModal}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors duration-200 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Submit a label
            </button>
          </div>
        </div>

        <div className="mb-6">
          {displayError && (
            <p className="text-red-600 text-sm mb-4">{displayError}</p>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex w-full mb-8">
          <button
            onClick={() => handleTabSwitch('all')}
            className={`flex-1 px-8 py-4 text-lg font-medium transition-all duration-200 ${
              activeTab === 'all'
                ? 'bg-violet-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } rounded-l-xl border-r border-gray-200`}
          >
            All
          </button>
          <button
            onClick={() => handleTabSwitch('discover')}
            className={`flex-1 px-8 py-4 text-lg font-medium transition-all duration-200 ${
              activeTab === 'discover'
                ? 'bg-violet-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } border-r border-gray-200`}
          >
            Discover
          </button>
          <button
            onClick={() => handleTabSwitch('following')}
            className={`flex-1 px-8 py-4 text-lg font-medium transition-all duration-200 ${
              activeTab === 'following'
                ? 'bg-violet-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } rounded-r-xl`}
          >
            Following
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-full mx-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-violet-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              name="search"
              id="search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search labels by name..."
              className="block w-full pl-10 pr-3 py-2.5 border border-violet-200 rounded-lg bg-violet-100/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-colors duration-200"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearchTerm('');
                }}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-violet-600 transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>

          {/* Filter indicators */}
          {selectedTag && (
            <div className="mt-4 mb-2 flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-gray-700 mr-1">Genre filter:</span>

              <span className="px-3 py-1.5 bg-violet-100 text-violet-800 rounded-lg flex items-center gap-1 text-sm font-medium shadow-sm border border-violet-200">
                {selectedTag}
                <button
                  onClick={() => setSelectedTag(null)}
                  className="ml-1.5 text-violet-500 hover:text-violet-800"
                  aria-label="Clear tag filter"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>

              {searchTerm && (
                <button
                  onClick={() => {
                    setSelectedTag(null);
                    setSearchTerm('');
                    setDebouncedSearchTerm('');
                  }}
                  className="px-3 py-1.5 bg-orange-400 text-gray-700 rounded-lg hover:bg-orange-200 transition-colors duration-100 text-sm font-medium flex items-center gap-1 shadow-sm"
                >
                  Clear all
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Quick info text below search bar */}
          <p className="text-center text-gray-500 text-sm mt-2">
            {totalCount !== null && `${totalCount} ${
              activeTab === 'following'
                ? 'followed'
                : activeTab === 'discover'
                ? 'available'
                : 'total'
            } labels`}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {labels.map((label, index) => {
            const isCurrentlyFollowed = followedLabels.has(label.id);

            return (
              <div
                key={label.id}
                ref={index === labels.length - 1 ? lastLabelElementRef : undefined}
                className={`bg-white rounded-lg border p-4 transition-all shadow-2xl duration-200 ${
                  isCurrentlyFollowed
                    ? 'border-violet-300 bg-violet-100/10 shadow-md' 
                    : 'border-gray-100 hover:border-violet-100'
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <h2 className="text-sm font-medium flex-1 min-w-0">
                    <a 
                      href={label.label_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-violet-600 transition-colors duration-200 truncate block"
                      title={label.label_name}
                    >
                      {label.label_name}
                    </a>
                  </h2>
                  
                  <button
                    onClick={() => toggleFollow(label.id)}
                    disabled={loadingLabelId === label.id}
                    className={`shrink-0 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 border shadow-sm ${
                      isCurrentlyFollowed
                        ? 'bg-violet-100 text-violet-700 border-violet-300 hover:bg-violet-200'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                    } ${loadingLabelId === label.id ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {loadingLabelId === label.id ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </span>
                    ) : (
                      isCurrentlyFollowed ? 'Following' : 'Follow'
                    )}
                  </button>
                </div>
                
                {/* Display tags if available */}
                {label.tags && label.tags.length > 0 && (
                  <>
                    <div className="border-t border-gray-200 my-2"></div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {label.tags.map((tag, i) => (
                        <button 
                          key={i}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent parent link click
                            e.stopPropagation(); // Prevent event bubbling
                            handleTagClick(tag);
                          }}
                          className={`inline-block px-2 py-0.5 text-xs font-normal rounded-md 
                            ${selectedTag === tag 
                              ? 'bg-violet-100 text-violet-700 border border-violet-300' 
                              : 'bg-gray-50 text-gray-500 border border-gray-100 hover:bg-gray-100 hover:text-gray-600'
                            } cursor-pointer transition-colors duration-150`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {isPageLoading && (
          <div className="flex justify-center mt-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
          </div>
        )}
        {!isPageLoading && labels.length === 0 && (
          <div className="text-center mt-8 text-gray-600">
            {activeTab === 'following'
              ? "You're not following any labels yet. Discover new labels in the Discover tab!"
              : activeTab === 'discover'
              ? "No labels found to discover. Try adjusting your filters."
              : "No labels found. Try adjusting your filters."}
          </div>
        )}

        {/* Submit Label Modal */}
        {isModalOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            aria-modal="true"
            role="dialog"
            aria-labelledby="modal-title"
          >
            <div 
              ref={modalRef}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl transform transition-all"
              onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
            >
              <div className="flex justify-between items-center mb-4">
                <h2 id="modal-title" className="text-xl font-bold text-gray-800">Submit a new label</h2>
                <button 
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close modal"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmitLabel}>
                <div className="mb-4">
                  <label htmlFor="labelUrl" className="block text-sm font-medium text-gray-700 mb-2">
                    Bandcamp Label URL
                  </label>
                  <input
                    type="url"
                    id="labelUrl"
                    value={labelUrl}
                    onChange={(e) => setLabelUrl(e.target.value)}
                    placeholder="https://labelname.bandcamp.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    required
                    autoFocus // Automatically focus the input when modal opens
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Please enter the URL of a Bandcamp label page (e.g., "https://baroquesunburst.bandcamp.com"). 
                    Submitted labels will be processed and validated before being added to the database every day. When added, the label will show up in the Discover tab.
                  </p>
                </div>
                
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Labels; 